import { useEffect, useState } from 'react';
import { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const PAYMENT_METHODS = ['CASH', 'CARD', 'INTERAC', 'E_TRANSFER', 'ONLINE'];

// Shared by the admin Pending Payments page and the POS Pending Payments screen — same backend
// endpoint (/subscriptions/:id/activate), works with either a full staff session or a POS PIN session.
export default function ActivateSubscriptionDialog({ api, subscriptionId, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [method, setMethod] = useState('CASH');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/subscriptions/${subscriptionId}`).then(({ data }) => setDetail(data.subscription));
  }, [api, subscriptionId]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/subscriptions/${subscriptionId}/activate`, { method, referenceNumber, notes });
      toast.success('Payment confirmed — subscription is now active');
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="activate-subscription-dialog">
        <DialogHeader>
          <DialogTitle>Confirm Payment &amp; Activate</DialogTitle>
          <DialogDescription>Record the payment method and activate this customer&apos;s subscription.</DialogDescription>
        </DialogHeader>

        {!detail ? (
          <p className="text-sm text-muted-ink">Loading…</p>
        ) : (
          <div className="space-y-3">
            <div className="text-sm">
              <p className="font-semibold text-charcoal">{detail.customer.name}</p>
              <p className="text-muted-ink">{detail.customer.phone} · {detail.planNameSnapshot}</p>
            </div>
            <div className="rounded-xl bg-bone p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-ink">Plan Price</span><span>${Number(detail.planPriceSnapshot).toFixed(2)}</span></div>
              {detail.subscriptionItems.map((it) => (
                <div key={it.id} className="flex justify-between"><span className="text-muted-ink">{it.itemName}</span><span>${Number(it.itemPrice).toFixed(2)}</span></div>
              ))}
              {Number(detail.discountAmount) > 0 && (
                <div className="flex justify-between"><span className="text-muted-ink">Discount {detail.discountCodeRef ? `(${detail.discountCodeRef.code})` : ''}</span><span>-${Number(detail.discountAmount).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between pt-1 border-t border-border/40 font-semibold">
                <span>Amount Due</span>
                <span data-testid="activate-final-amount">${Number(detail.finalAmount).toFixed(2)}</span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Payment Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="activate-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Reference (optional)</Label>
              <Input data-testid="activate-reference-input" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea data-testid="activate-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button data-testid="activate-confirm-btn" disabled={!detail || busy} onClick={confirm} className="bg-saffron hover:bg-saffron-hover">
            {busy ? 'Activating…' : 'Confirm Payment & Activate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
